import React, { useState } from 'react';
import { User, Star, Clock, Edit, Trash2, Plus, Search, Filter } from 'lucide-react';
import { HealthProfessional } from '../../types';

interface HealthProfessionalsListProps {
  professionals: HealthProfessional[];
  onEdit: (professional: HealthProfessional) => void;
  onDelete: (id: string) => void;
  onAdd: () => void;
  isLoading?: boolean;
}

const HealthProfessionalsList: React.FC<HealthProfessionalsListProps> = ({
  professionals,
  onEdit,
  onDelete,
  onAdd,
  isLoading = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAvailable, setFilterAvailable] = useState<'all' | 'available' | 'unavailable'>('all');
  const [sortBy, setSortBy] = useState<'name' | 'rating' | 'experience'>('name');

  const filteredAndSortedProfessionals = professionals
    .filter(prof => {
      const matchesSearch = prof.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           prof.practiceFocus.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           prof.specialties.some(s => s.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesFilter = filterAvailable === 'all' ||
                           (filterAvailable === 'available' && prof.availableToday) ||
                           (filterAvailable === 'unavailable' && !prof.availableToday);
      
      return matchesSearch && matchesFilter;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'rating':
          return (b.rating || 0) - (a.rating || 0);
        case 'experience':
          return (b.yearsExperience || 0) - (a.yearsExperience || 0);
        default:
          return 0;
      }
    });

  const handleDelete = (professional: HealthProfessional) => {
    if (window.confirm(`Are you sure you want to delete ${professional.name}? This action cannot be undone.`)) {
      onDelete(professional.id);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-luma-text">Health Professionals</h1>
          <p className="text-gray-600">Manage your team of health professionals</p>
        </div>
        
        <button
          onClick={onAdd}
          className="bg-luma-primary text-white px-4 py-2 rounded-lg hover:bg-luma-primary-dark transition-colors flex items-center"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Professional
        </button>
      </div>

      {/* Filters and Search */}
      <div className="bg-white p-4 rounded-2xl shadow-sm border space-y-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search by name, focus, or specialty..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-luma-primary focus:border-transparent"
            />
          </div>

          {/* Availability Filter */}
          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <select
              value={filterAvailable}
              onChange={(e) => setFilterAvailable(e.target.value as any)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-luma-primary focus:border-transparent"
            >
              <option value="all">All</option>
              <option value="available">Available Today</option>
              <option value="unavailable">Not Available</option>
            </select>
          </div>

          {/* Sort */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-luma-primary focus:border-transparent"
          >
            <option value="name">Sort by Name</option>
            <option value="rating">Sort by Rating</option>
            <option value="experience">Sort by Experience</option>
          </select>
        </div>

        <div className="text-sm text-gray-600">
          Showing {filteredAndSortedProfessionals.length} of {professionals.length} professionals
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-luma-primary"></div>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && professionals.length === 0 && (
        <div className="text-center py-12">
          <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No health professionals found</h3>
          <p className="text-gray-600 mb-4">Get started by adding your first health professional.</p>
          <button
            onClick={onAdd}
            className="bg-luma-primary text-white px-4 py-2 rounded-lg hover:bg-luma-primary-dark transition-colors inline-flex items-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Professional
          </button>
        </div>
      )}

      {/* No Results */}
      {!isLoading && professionals.length > 0 && filteredAndSortedProfessionals.length === 0 && (
        <div className="text-center py-8">
          <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No results found</h3>
          <p className="text-gray-600">Try adjusting your search or filter criteria.</p>
        </div>
      )}

      {/* Professionals Grid */}
      {!isLoading && filteredAndSortedProfessionals.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredAndSortedProfessionals.map((professional) => (
            <div key={professional.id} className="bg-white rounded-2xl shadow-sm border hover:shadow-md transition-shadow">
              <div className="p-6">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    {professional.photoUrl ? (
                      <img
                        src={professional.photoUrl}
                        alt={professional.name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-luma-primary bg-opacity-10 rounded-full flex items-center justify-center">
                        <User className="w-6 h-6 text-luma-primary" />
                      </div>
                    )}
                    <div>
                      <h3 className="font-semibold text-luma-text">{professional.name}</h3>
                      <p className="text-sm text-gray-600">{professional.practiceFocus}</p>
                    </div>
                  </div>
                  
                  <div className="flex space-x-1">
                    <button
                      onClick={() => onEdit(professional)}
                      className="p-2 text-gray-400 hover:text-luma-primary transition-colors"
                      title="Edit professional"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(professional)}
                      className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                      title="Delete professional"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Details */}
                <div className="space-y-3">
                  {/* Rating and Experience */}
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center space-x-1">
                      <Star className="w-4 h-4 text-yellow-400 fill-current" />
                      <span className="font-medium">{(professional.rating || 0).toFixed(1)}</span>
                    </div>
                    <span className="text-gray-600">{professional.yearsExperience || 0} years exp.</span>
                  </div>

                  {/* Availability */}
                  <div className="flex items-center space-x-2">
                    <div className={`w-2 h-2 rounded-full ${professional.availableToday ? 'bg-green-500' : 'bg-gray-400'}`} />
                    <span className={`text-sm ${professional.availableToday ? 'text-green-700' : 'text-gray-600'}`}>
                      {professional.availableToday ? 'Available today' : 'Not available'}
                    </span>
                  </div>

                  {/* Callback Time */}
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <Clock className="w-4 h-4" />
                    <span>{professional.callbackTime}</span>
                  </div>

                  {/* Specialties */}
                  <div className="flex flex-wrap gap-1">
                    {professional.specialties.slice(0, 3).map((specialty, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-luma-primary bg-opacity-10 text-luma-primary text-xs rounded-full"
                      >
                        {specialty}
                      </span>
                    ))}
                    {professional.specialties.length > 3 && (
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                        +{professional.specialties.length - 3} more
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default HealthProfessionalsList;